import React, {useCallback, useEffect, useMemo, useState} from 'react'
import AsyncSelect from 'react-select/async'
import {TenantsClient, TenantsQueryParams} from '../../../infrastracture/api/TenantsClient'
import {TenantTypeIds} from '../../constants/tenantTypeIds'
import customStyles from './customStyles'

interface Props {
  tenantTypeIds?: string[]
  name?: string
  defaultValue?: any
  defaultOptions?: any
  placeholder?: string
  disabled?: boolean
  excludeManagedSites?: boolean
  onChange?: (
    name: any,
    value: any,
    label: any,
    operationalClient: any,
    departments?: any,
    externalProjectId?: any,
    suppliedPurchaseOrder?: any,
    isWoDeptRequired?: boolean,
    enableQuoteSummary?: boolean
  ) => void
  id?: string
  removeClients?: boolean
  removeContractors?: boolean
  removeOCContractors?: boolean
  includeAll?: boolean
  noDefault?: boolean
  clientWOUpdateUserId?: string
  className?: string
  populateManagers?: (managers: any) => void
  clientTenantId?: string
}

const Tenantselector: React.FC<Props> = (props: any) => {
  const {
    id,
    defaultValue,
    placeholder,
    disabled,
    name,
    removeClients,
    includeAll,
    noDefault,
    removeContractors,
    tenantTypeIds,
    removeOCContractors,
    clientWOUpdateUserId,
    populateManagers,
    className,
    excludeManagedSites,
    clientTenantId,
  } = props

  const [defaultOptions, setDefaultOptions] = useState<Array<any>>([])
  const [value, setValue] = useState<any>({})

  const defaultId = id || 'tenantId'

  // Memoize tenantTypeIds to prevent unnecessary re-fetches when array reference changes but values are the same
  const tenantTypeIdsKey = useMemo(() => tenantTypeIds?.join(',') || '', [tenantTypeIds])

  useEffect(() => {
    if (!props.defaultOptions) {
      if (!noDefault) {
        let query = new TenantsQueryParams()
          .Sort('lastUsed', 'desc')
          .Paginate(0, 20)
          .WithEnabled(true)

        // Default to Contractors if no tenantTypeIds provided and removeClients is not set
        if (tenantTypeIds && tenantTypeIds.length > 0) {
          query.WithTenantTypeIds(tenantTypeIds)
        } else if (!removeClients) {
          query.WithTenantTypeIds([TenantTypeIds.Contractor])
        }
        if (excludeManagedSites) query.WithExcludeManagedSites(true)
        if (clientTenantId) query.WithClientTenantId(clientTenantId)

        TenantsClient.List(query).then((response) => {
          let options = response.data.data.map((item: any) => {
            return {
              entity: item,
              value: item.id,
              label: item.name,
            }
          })
          if (includeAll) options = [{value: 'all', label: 'All'}, ...options]

          setDefaultOptions(options)
        })
      }
    } else {
      let options = [...props.defaultOptions]
      if (includeAll) options = [{value: 'all', label: 'All'}, ...options]

      setDefaultOptions(options)
    }
  }, [clientTenantId, tenantTypeIdsKey, excludeManagedSites, includeAll, noDefault, removeClients])

  useEffect(() => {
    if (defaultValue && defaultValue?.label) {
      setValue(defaultValue)
    } else if (value !== defaultValue) setValue(defaultValue)
  }, [defaultValue])

  const promiseOptions = useCallback(
    (inputValue: string) =>
      new Promise<any[]>((resolve) => {
        let query = new TenantsQueryParams()
          .Search(inputValue)
          .Sort('name', 'asc')
          .Paginate(0, 20)
          .WithEnabled(true)

        if (clientWOUpdateUserId) query.WithClientWOUpdateUserId(clientWOUpdateUserId)

        if (removeClients) query.WithRemoveClients(true)
        if (removeContractors) query.WithRemoveContractors(true)
        if (removeOCContractors) query.WithRemoveOCContractors(true)
        // Default to Contractors if no tenantTypeIds provided and removeClients is not set
        if (tenantTypeIds && tenantTypeIds.length > 0) {
          query.WithTenantTypeIds(tenantTypeIds)
        }

        if (excludeManagedSites) query.WithExcludeManagedSites(true)
        if (clientTenantId) query.WithClientTenantId(clientTenantId)

        let data = TenantsClient.List(query).then((response) => {
          let options = response.data.data.map((item: any) => {
            return {
              entity: item,
              value: item.id,
              label: item.name,
            }
          })

          if (includeAll) options = [{value: 'all', label: 'All'}, ...options]

          return options
        })

        resolve(data)
      }),
    [
      clientWOUpdateUserId,
      removeClients,
      removeContractors,
      removeOCContractors,
      excludeManagedSites,
      clientTenantId,
      includeAll,
      tenantTypeIds,
    ]
  )

  const handleChange = async (values: any) => {
    const {onChange} = props

    if (!values) {
      setValue(null)
      onChange?.(defaultId, 'all', 'All')
      return
    }

    const {label, value, entity} = values
    setValue(values)

    console.log('values')
    console.log(values)

    onChange?.(
      defaultId,
      value,
      label,
      entity?.operationalClient,
      entity?.departments,
      entity?.externalProjectId,
      entity?.suppliedPurchaseOrder,
      entity?.isWoDeptRequired,
      entity?.enableQuoteSummary
    )

    if (value === 'all' || !entity) return

    let managers = await TenantsClient.GetManagers(value)

    if (populateManagers) {
      if (managers.data && managers.data.length > 0) populateManagers(managers.data)
      else populateManagers([])
    }
  }

  return (
    <AsyncSelect
      key={`tenant-${clientTenantId || 'default'}-${tenantTypeIds?.join(',') || 'contractor'}`}
      className={className}
      onChange={handleChange}
      value={value}
      loadOptions={promiseOptions}
      defaultOptions={defaultOptions.length > 0 ? defaultOptions : true}
      placeholder={placeholder}
      isDisabled={disabled}
      id={name ? name : defaultId}
      name={name ? name : defaultId}
      styles={customStyles}
      menuPortalTarget={document.body}
    />
  )
}

export default Tenantselector
